import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

// Per-environment RUM configuration — mirrors the pattern from the STO (Ember) project.
// Session replay is disabled across all environments (matching v1 config).
// Re-enable with sessionReplaySampleRate: 100 (staging) / 20 (production) when ready.
const RUM_ENV_CONFIG = {
	development: {
		sessionSampleRate: 0, // % of total sessions tracked by RUM (errors, metrics, interactions)
		sessionReplaySampleRate: 0, // % of already-sampled sessions that also get a DOM replay recording
		defaultPrivacyLevel: 'allow' as const,
	},
	staging: {
		sessionSampleRate: 100,
		sessionReplaySampleRate: 0,
		defaultPrivacyLevel: 'mask-user-input' as const,
	},
	production: {
		sessionSampleRate: 100,
		sessionReplaySampleRate: 0,
		defaultPrivacyLevel: 'mask-user-input' as const,
	},
} as const

type AppEnv = keyof typeof RUM_ENV_CONFIG

const appEnv: AppEnv =
	import.meta.env.VITE_APP_ENV in RUM_ENV_CONFIG ? import.meta.env.VITE_APP_ENV : 'development'

const rumEnvConfig = RUM_ENV_CONFIG[appEnv]

/** True when Datadog Logs is configured. Uses its own dedicated client token. */
export const isDatadogLogsEnabled = (): boolean =>
	Boolean(import.meta.env.VITE_DATADOG_CLIENT_TOKEN)

/**
 * True when Datadog RUM is fully configured.
 * RUM uses a separate client token from Logs — do not share VITE_DATADOG_CLIENT_TOKEN here.
 */
export const isDatadogRUMEnabled = (): boolean =>
	Boolean(
		import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN &&
		import.meta.env.VITE_DATADOG_APPLICATION_ID,
	)

export function initDatadogLogs(): void {
	if (!isDatadogLogsEnabled()) return

	datadogLogs.init({
		clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
		site: 'datadoghq.com',
		service: 'online-ordering-v2',
		env: appEnv,
		forwardErrorsToLogs: true,
		sessionSampleRate: 100,
	})

	datadogLogs.setGlobalContext({ department: 'cloud-and-mobile', platform: 'lighthouse' })

	// Forward uncaught JS errors to Datadog Logs (distinct from the RUM channel that
	const previousHandler = window.onerror ?? null
	window.onerror = function (message, source, lineno, colno, error) {
		let serialized: string
		try {
			serialized = JSON.stringify({ message, source, lineno, colno, error })
		} catch {
			serialized = JSON.stringify({
				message,
				source,
				lineno,
				colno,
				name: error?.name,
			})
		}
		datadogLogs.logger.error(`Global error: ${serialized}`)

		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return previousHandler ? previousHandler(message, source, lineno, colno, error) : false
	}
}

export function initDatadogRUM(): void {
	if (!isDatadogRUMEnabled()) return

	datadogRum.init({
		applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
		clientToken: import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN,
		site: 'datadoghq.com',
		service: 'online-ordering-v2',
		env: appEnv,
		version: import.meta.env.VITE_APP_VERSION,
		sessionSampleRate: rumEnvConfig.sessionSampleRate,
		sessionReplaySampleRate: rumEnvConfig.sessionReplaySampleRate,
		trackUserInteractions: true,
		trackResources: true,
		trackLongTasks: true,
		defaultPrivacyLevel: rumEnvConfig.defaultPrivacyLevel,
	})

	datadogRum.startSessionReplayRecording()
	datadogRum.setGlobalContext({ department: 'cloud-and-mobile', platform: 'lighthouse' })
}
